import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  Label,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1"></div>;

        if (trimmed.startsWith("### "))
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">
                •
              </span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg className="animate-spin h-5 w-5 text-[#59AD31]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

// ───────────────────── Labels (ES) ─────────────────────
const xAxisLabelYear = "Years";
const yAxisLabelUsd = "Million USD";
const yAxisDxUsd = -yAxisLabelUsd.length;

function formatYAxisMillionsTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  const abs = Math.abs(n);
  const inMillions = abs >= 1_000_000 ? abs / 1_000_000 : abs;
  const maxFD = inMillions < 10 ? 2 : 1;

  return inMillions.toLocaleString("en-US", {
    maximumFractionDigits: maxFD,
  });
}

// ───────── helper para ordenar series por valor máximo ─────────
function getOrderByMaxValue(data, keys) {
  if (!data || data.length === 0) return keys;

  const regularKeys = [...keys].filter((key) => key !== "others");
  const othersKeys = [...keys].filter((key) => key === "others");

  const sortedRegularKeys = regularKeys.sort((a, b) => {
    const maxA = Math.max(
      ...data.map((d) => (typeof d[a] === "number" ? d[a] : 0)),
    );
    const maxB = Math.max(
      ...data.map((d) => (typeof d[b] === "number" ? d[b] : 0)),
    );
    return maxB - maxA;
  });

  return [...sortedRegularKeys, ...othersKeys];
}

// ───────────────────── tooltip custom (ya ordenado por valor) ─────────────────────
function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item) => {
        const isTarget = item.dataKey === "targetShare";
        let icon;

        if (isTarget) {
          icon = (
            <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
              <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
            </svg>
          );
        } else if (chartType === "line") {
          icon = (
            <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
              <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" />
            </svg>
          );
        } else {
          icon = (
            <span
              className="h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
          );
        }

        return (
          <div
            key={item.dataKey}
            className="mb-0.5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-1.5">
              {icon}
              <span className="text-[#7E8081]">{item.name}</span>
            </div>
            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? Math.abs(item.value).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                  })
                : item.value}{" "}
              <span className="text-[#98999A]">{yAxisLabelUsd}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ───────── leyendas custom SOLO para ordenar las etiquetas ─────────
function OrderedLegend({ payload, order, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;

  const ordered = [...payload].sort(
    (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey),
  );

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
            title="Click para mostrar/ocultar"
          >
            <span className="text-[12px]">{entry.value}</span>
            {chartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color || "#9999CC"} strokeWidth="2" />
              </svg>
            ) : (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ───────── colores únicos por key ─────────
function colorForKey(key, index = 0) {
  if (key === "others") return "#9CA3AF";

  const COLOR_POOL = [
    "#80D5FF",
    "#FB752E",
    "#FFD786",
    "#FF5579",
    "#009B8A",
    "#CBF3DD",
    "#7C3AED",
    "#F59E0B",
    "#ffcbcb",
    "#EF4444",
    "#2563EB",
    "#A855F7",
    "#14B8A6",
    "#F97316",
    "#84CC16",
    "#E11D48",
  ];

  return COLOR_POOL[index % COLOR_POOL.length];
}

function displayName(key) {
  if (key === "others") return "Others";

  const normalized = String(key)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return normalized
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export default function TradeTab({ data, pathway, pathwayId, country, onLoadingChange }) {
  const [hiddenTradeKeys, setHiddenTradeKeys] = useState([]);
  const [hiddenExportKeys, setHiddenExportKeys] = useState([]);
  const [hiddenImportKeys, setHiddenImportKeys] = useState([]);
  const [tradeChartMode, setTradeChartMode] = useState("bar");
  const [exportsChartMode, setExportsChartMode] = useState("bar");
  const [importsChartMode, setImportsChartMode] = useState("bar");

  // 🔄 Limpiar estados cuando cambian los datos
  useEffect(() => {
    setHiddenTradeKeys([]);
    setHiddenExportKeys([]);
    setHiddenImportKeys([]);
  }, [data]);

  const tradeBalanceRaw = data ? data["Trade balance"] : [];

  const tradeBalanceData = useMemo(
    () =>
      (tradeBalanceRaw ?? []).map((d) => ({
        year: d.year,
        exports: Number(d.exports) || 0,
        imports: -(Number(d.imports) || 0),
       
      })),
    [tradeBalanceRaw],
  );

  const exportsData = data ? data["Exports by main products"] : [];
  const importsData = data ? data["Imports by main products"] : [];

  // ✅ keys dinámicas: todo lo que venga en la fila menos year
  const exportKeys = useMemo(() => {
    const first = exportsData?.[0];
    if (!first) return [];
    const keys = Object.keys(first).filter((k) => k !== "year");
    // pon "others" al final si existe
    return keys.sort((a, b) => (a === "others" ? 1 : b === "others" ? -1 : 0));
  }, [exportsData]);

  const importKeys = useMemo(() => {
    const first = importsData?.[0];
    if (!first) return [];
    const keys = Object.keys(first).filter((k) => k !== "year");
    return keys.sort((a, b) => (a === "others" ? 1 : b === "others" ? -1 : 0));
  }, [importsData]);

  // Orden de leyendas por máximo valor
  const tradeLegendOrder = useMemo(
    () => getOrderByMaxValue(tradeBalanceData, ["imports", "exports"]),
    [tradeBalanceData],
  );

  const exportsLegendOrder = useMemo(
    () => getOrderByMaxValue(exportsData, exportKeys),
    [exportsData, exportKeys],
  );

  const importsLegendOrder = useMemo(
    () => getOrderByMaxValue(importsData, importKeys),
    [importsData, importKeys],
  );

  const [tradeAnalysis, setTradeAnalysis] = useState("");
  const [exportsAnalysis, setExportsAnalysis] = useState("");
  const [importsAnalysis, setImportsAnalysis] = useState("");
  const [loadingTradeAnalysis, setLoadingTradeAnalysis] = useState(false);
  const [loadingExportsAnalysis, setLoadingExportsAnalysis] = useState(false);
  const [loadingImportsAnalysis, setLoadingImportsAnalysis] = useState(false);

  useEffect(() => {
    onLoadingChange?.(loadingTradeAnalysis || loadingExportsAnalysis || loadingImportsAnalysis);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTradeAnalysis, loadingExportsAnalysis, loadingImportsAnalysis]);

  const fetchTradeAnalysis = async () => {
    if (!tradeBalanceData || tradeBalanceData.length === 0 || !pathwayId || !country) {
      setTradeAnalysis("");
      setLoadingTradeAnalysis(false);
      return;
    }

    setLoadingTradeAnalysis(true);
    try {
      const payload = {
        analysisKey: "trade_balance_analysis",
        scope: "country",
        country: country,
        tradeBalanceData: {
          data: JSON.stringify(tradeBalanceData),
          title: `Trade balance - ${country}`,
        },
        context: "trade",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Trade balance",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);
      const resData = await response.json();
      setTradeAnalysis(extractAnalysisText(resData, ["tradeBalanceAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching trade balance analysis:", error);
      setTradeAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingTradeAnalysis(false);
    }
  };

  const fetchExportsAnalysis = async () => {
    if (!exportsData || exportsData.length === 0 || !pathwayId || !country) {
      setExportsAnalysis("");
      setLoadingExportsAnalysis(false);
      return;
    }

    setLoadingExportsAnalysis(true);
    try {
      const payload = {
        analysisKey: "exports_analysis",
        scope: "country",
        country: country,
        exportsData: {
          data: JSON.stringify(exportsData),
          title: `Exports by main products - ${country}`,
        },
        context: "trade",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Exports by main products",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);
      const resData = await response.json();
      setExportsAnalysis(extractAnalysisText(resData, ["exportsAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching exports analysis:", error);
      setExportsAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingExportsAnalysis(false);
    }
  };

  const fetchImportsAnalysis = async () => {
    if (!importsData || importsData.length === 0 || !pathwayId || !country) {
      setImportsAnalysis("");
      setLoadingImportsAnalysis(false);
      return;
    }

    setLoadingImportsAnalysis(true);
    try {
      const payload = {
        analysisKey: "imports_analysis",
        scope: "country",
        country: country,
        importsData: {
          data: JSON.stringify(importsData),
          title: `Imports by main products - ${country}`,
        },
        context: "trade",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "Imports by main products",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Analysis API error: ${response.status}`);
      const resData = await response.json();
      setImportsAnalysis(extractAnalysisText(resData, ["importsAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching imports analysis:", error);
      setImportsAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingImportsAnalysis(false);
    }
  };

  useEffect(() => {
    fetchTradeAnalysis();
    // eslint-disable-next-line
  }, [tradeBalanceData, pathway, pathwayId, country]);

  useEffect(() => {
    fetchExportsAnalysis();
    // eslint-disable-next-line
  }, [exportsData, pathway, pathwayId, country]);

  useEffect(() => {
    fetchImportsAnalysis();
    // eslint-disable-next-line
  }, [importsData, pathway, pathwayId, country]);

  const handleLegendClickTrade = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenTradeKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickExports = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenExportKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const handleLegendClickImports = (o) => {
    const { dataKey } = o || {};
    if (!dataKey) return;
    setHiddenImportKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey],
    );
  };

  const hasTradeBalance = tradeBalanceData?.length > 0;
  const hasExports = exportsData?.length > 0 && exportKeys?.length > 0;
  const hasImports = importsData?.length > 0 && importKeys?.length > 0;

  return (
    <div className="flex w-full flex-col gap-10">
      {/* ─── BLOQUE 1: Trade balance ─── */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Commodities Trade
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "bar", label: "Bar" },
                { value: "line", label: "Line" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = tradeChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setTradeChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                const chartSection = document.getElementById('trade-balance-chart-section');
                if (!chartSection) return;
                const description = 'Commodities Trade';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                const description = 'Commodities Trade';
                const Papa = (await import('papaparse')).default;
                if (!tradeBalanceData || !Array.isArray(tradeBalanceData) || tradeBalanceData.length === 0) return;
                const headers = Object.keys(tradeBalanceData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...tradeBalanceData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="trade-balance-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasTradeBalance ? (
            <ResponsiveContainer width="100%" height="100%">
              {tradeChartMode === "bar" ? (
                <BarChart
                  data={tradeBalanceData}
                  stackOffset="sign"
                  margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="year"
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickMargin={10}
                  >
                    <Label
                      value={xAxisLabelYear}
                      position="insideBottom"
                      offset={-20}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </XAxis>

                  <YAxis
                    width={60}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickFormatter={(v) => {
                      const n = Number(v);
                      if (!Number.isFinite(n)) return "";

                      const abs = Math.abs(n);
                      const inMillions = abs >= 1_000_000 ? abs / 1_000_000 : abs;
                      const maxFD = inMillions < 10 ? 2 : 1;

                      return inMillions.toLocaleString("en-US", {
                        maximumFractionDigits: maxFD,
                      });
                    }}
                  >
                    <Label
                      value={yAxisLabelUsd}
                      angle={-90}
                      position="left"
                      dx={yAxisDxUsd}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </YAxis>

                  <Tooltip
                    content={(props) => <CustomTooltip {...props} chartType="bar" />}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />

                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ top: 0, left: 0 }}
                    content={(props) => (
                      <OrderedLegend
                        {...props}
                        order={tradeLegendOrder}
                        hiddenKeys={hiddenTradeKeys}
                        onToggle={handleLegendClickTrade}
                        chartType="bar"
                      />
                    )}
                  />

                  <Bar
                    dataKey="imports"
                    name="Imports"
                    fill="#80D5FF"
                    stackId="stack"
                    hide={hiddenTradeKeys.includes("imports")}
                  />
                  <Bar
                    dataKey="exports"
                    name="Exports"
                    fill="#FB752E"
                    stackId="stack"
                    hide={hiddenTradeKeys.includes("exports")}
                  />
                </BarChart>
              ) : (
                <ComposedChart
                  data={tradeBalanceData}
                  margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="year"
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickMargin={10}
                  >
                    <Label
                      value={xAxisLabelYear}
                      position="insideBottom"
                      offset={-20}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </XAxis>

                  <YAxis
                    width={60}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickFormatter={(v) => {
                      const n = Number(v);
                      if (!Number.isFinite(n)) return "";

                      const abs = Math.abs(n);
                      const inMillions = abs >= 1_000_000 ? abs / 1_000_000 : abs;
                      const maxFD = inMillions < 10 ? 2 : 1;

                      return inMillions.toLocaleString("en-US", {
                        maximumFractionDigits: maxFD,
                      });
                    }}
                  >
                    <Label
                      value={yAxisLabelUsd}
                      angle={-90}
                      position="left"
                      dx={yAxisDxUsd}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </YAxis>

                  <Tooltip
                    content={(props) => <CustomTooltip {...props} chartType={tradeChartMode} />}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />

                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ top: 0, left: 0 }}
                    content={(props) => (
                      <OrderedLegend
                        {...props}
                        order={tradeLegendOrder}
                        hiddenKeys={hiddenTradeKeys}
                        onToggle={handleLegendClickTrade}
                        chartType={tradeChartMode}
                      />
                    )}
                  />

                  {tradeChartMode === "line" ? (
                    <>
                      <Line
                        type="monotone"
                        dataKey="imports"
                        name="Imports"
                        stroke="#80D5FF"
                        strokeWidth={3}
                        dot={{ r: 2 }}
                        hide={hiddenTradeKeys.includes("imports")}
                      />
                      <Line
                        type="monotone"
                        dataKey="exports"
                        name="Exports"
                        stroke="#FB752E"
                        strokeWidth={3}
                        dot={{ r: 2 }}
                        hide={hiddenTradeKeys.includes("exports")}
                      />
                    </>
                  ) : (
                    <>
                      <Area
                        type="monotone"
                        dataKey="imports"
                        name="Imports"
                        stroke="#80D5FF"
                        fill="#80D5FF"
                        fillOpacity={0.25}
                        strokeWidth={2}
                        dot={false}
                        hide={hiddenTradeKeys.includes("imports")}
                      />
                      <Area
                        type="monotone"
                        dataKey="exports"
                        name="Exports"
                        stroke="#FB752E"
                        fill="#FB752E"
                        fillOpacity={0.25}
                        strokeWidth={2}
                        dot={false}
                        hide={hiddenTradeKeys.includes("exports")}
                      />
                    </>
                  )}
                </ComposedChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No trade balance data available.
            </div>
          )}
        </div>

        <div className="w-full min-h-[100px]">
          {loadingTradeAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchTradeAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_commodities_trade"><NativeAnalysis content={tradeAnalysis} /></div>
            </div>
          )}
        </div>
      </section>

      {/* ─── BLOQUE 2: Exports by main products ─── */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Exports of main commodities
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "bar", label: "Bar" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = exportsChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setExportsChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                // Download image as JPG using html2canvas (chart only)
                const chartSection = document.getElementById('exports-chart-section');
                if (!chartSection) return;
                const description = 'Exports of main commodities';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                // Download data as CSV using papaparse, better formatted
                const description = 'Exports of main commodities';
                const Papa = (await import('papaparse')).default;
                if (!exportsData || !Array.isArray(exportsData) || exportsData.length === 0) return;
                // Readable headers
                const headers = Object.keys(exportsData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...exportsData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="exports-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasExports ? (
            <ResponsiveContainer width="100%" height="100%">
              {exportsChartMode === "bar" ? (
                <BarChart
                  data={exportsData}
                  stackOffset="none"
                  margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="year"
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickMargin={10}
                  >
                    <Label
                      value={xAxisLabelYear}
                      position="insideBottom"
                      offset={-20}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </XAxis>

                  <YAxis
                    width={50}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickFormatter={formatYAxisMillionsTick}
                  >
                    <Label
                      value={yAxisLabelUsd}
                      angle={-90}
                      position="left"
                      dx={yAxisDxUsd}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </YAxis>

                  <Tooltip
                    content={(props) => <CustomTooltip {...props} chartType="bar" />}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />

                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ top: 0, left: 0 }}
                    content={(props) => (
                      <OrderedLegend
                        {...props}
                        order={exportsLegendOrder}
                        hiddenKeys={hiddenExportKeys}
                        onToggle={handleLegendClickExports}
                        chartType="bar"
                      />
                    )}
                  />

                  {exportKeys.map((k, index) => (
                    <Bar
                      key={k}
                      dataKey={k}
                      stackId="a"
                      fill={colorForKey(k, index)}
                      name={displayName(k)}
                      hide={hiddenExportKeys.includes(k)}
                    />
                  ))}
                </BarChart>
              ) : (
                <ComposedChart
                  data={exportsData}
                  margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="year"
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickMargin={10}
                  >
                    <Label
                      value={xAxisLabelYear}
                      position="insideBottom"
                      offset={-20}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </XAxis>

                  <YAxis
                    width={50}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickFormatter={formatYAxisMillionsTick}
                  >
                    <Label
                      value={yAxisLabelUsd}
                      angle={-90}
                      position="left"
                      dx={yAxisDxUsd}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </YAxis>

                  <Tooltip
                    content={(props) => <CustomTooltip {...props} chartType="area" />}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />

                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ top: 0, left: 0 }}
                    content={(props) => (
                      <OrderedLegend
                        {...props}
                        order={exportsLegendOrder}
                        hiddenKeys={hiddenExportKeys}
                        onToggle={handleLegendClickExports}
                        chartType="area"
                      />
                    )}
                  />

                  {exportKeys.map((k, index) => (
                    <Area
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stackId="a"
                      stroke={colorForKey(k, index)}
                      fill={colorForKey(k, index)}
                      fillOpacity={0.25}
                      name={displayName(k)}
                      hide={hiddenExportKeys.includes(k)}
                    />
                  ))}
                </ComposedChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No exports data available.
            </div>
          )}
        </div>

        <div className="w-full min-h-[100px]">
          {loadingExportsAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchExportsAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_exports_main_commodities"><NativeAnalysis content={exportsAnalysis} /></div>
            </div>
          )}
        </div>
      </section>

      {/* ─── BLOQUE 3: Imports by main products ─── */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Imports of main commodities
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "bar", label: "Bar" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = importsChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setImportsChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                // Download image as JPG using html2canvas (chart only)
                const chartSection = document.getElementById('imports-chart-section');
                if (!chartSection) return;
                const description = 'Imports of main commodities';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                // Download data as CSV using papaparse, better formatted
                const description = 'Imports of main commodities';
                const Papa = (await import('papaparse')).default;
                if (!importsData || !Array.isArray(importsData) || importsData.length === 0) return;
                // Readable headers
                const headers = Object.keys(importsData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...importsData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="imports-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          {hasImports ? (
            <ResponsiveContainer width="100%" height="100%">
              {importsChartMode === "bar" ? (
                <BarChart
                  data={importsData}
                  stackOffset="none"
                  margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="year"
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickMargin={10}
                  >
                    <Label
                      value={xAxisLabelYear}
                      position="insideBottom"
                      offset={-20}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </XAxis>

                  <YAxis
                    width={50}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickFormatter={formatYAxisMillionsTick}
                  >
                    <Label
                      value={yAxisLabelUsd}
                      angle={-90}
                      position="left"
                      dx={yAxisDxUsd}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </YAxis>

                  <Tooltip
                    content={(props) => <CustomTooltip {...props} chartType="bar" />}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />

                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ top: 0, left: 0 }}
                    content={(props) => (
                      <OrderedLegend
                        {...props}
                        order={importsLegendOrder}
                        hiddenKeys={hiddenImportKeys}
                        onToggle={handleLegendClickImports}
                        chartType="bar"
                      />
                    )}
                  />

                  {importKeys.map((k, index) => (
                    <Bar
                      key={k}
                      dataKey={k}
                      stackId="a"
                      fill={colorForKey(k, index)}
                      name={displayName(k)}
                      hide={hiddenImportKeys.includes(k)}
                    />
                  ))}
                </BarChart>
              ) : (
                <ComposedChart
                  data={importsData}
                  margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis
                    dataKey="year"
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickMargin={10}
                  >
                    <Label
                      value={xAxisLabelYear}
                      position="insideBottom"
                      offset={-20}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </XAxis>

                  <YAxis
                    width={50}
                    tick={{ fill: "#98999A", fontSize: 12 }}
                    tickFormatter={formatYAxisMillionsTick}
                  >
                    <Label
                      value={yAxisLabelUsd}
                      angle={-90}
                      position="left"
                      dx={yAxisDxUsd}
                      style={{ fill: "#98999A", fontSize: 15 }}
                    />
                  </YAxis>

                  <Tooltip
                    content={(props) => <CustomTooltip {...props} chartType="area" />}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />

                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ top: 0, left: 0 }}
                    content={(props) => (
                      <OrderedLegend
                        {...props}
                        order={importsLegendOrder}
                        hiddenKeys={hiddenImportKeys}
                        onToggle={handleLegendClickImports}
                        chartType="area"
                      />
                    )}
                  />

                  {importKeys.map((k, index) => (
                    <Area
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stackId="a"
                      stroke={colorForKey(k, index)}
                      fill={colorForKey(k, index)}
                      fillOpacity={0.25}
                      name={displayName(k)}
                      hide={hiddenImportKeys.includes(k)}
                    />
                  ))}
                </ComposedChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[#7E8081]">
              No imports data available.
            </div>
          )}
        </div>

        <div className="w-full min-h-[100px]">
          {loadingImportsAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchImportsAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_imports_main_commodities"><NativeAnalysis content={importsAnalysis} /></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
